import { useId } from "react";
import type { ReactNode } from "react";

import { Tag } from "../lib/Tag.tsx";
import { FieldList } from "./ProfilePanels.tsx";
import type { IdentityCard } from "./profile.model.ts";

/**
 * Who this record is about.
 *
 * One component for both subjects. A person and an organisation are different
 * records with different writers, but the card that names them is the same
 * shape — a tile, a name, one line of context, whether the registry has
 * verified it, and a short list of identifiers — so building two would be
 * building the same thing twice and letting them drift.
 *
 * The tile is square. Every other corner in ONDO is, and the circle in the
 * prototype was the one place the shell disagreed with itself.
 *
 * The tag is not decoration: `verifiedLabel` says "Verified" or "Not yet
 * verified" in words. On a registry whose product is verification, an absent
 * tick must not be indistinguishable from a rendering fault.
 */
export function ProfileIdentity({
  card,
  action,
  children,
}: {
  card: IdentityCard;
  /** The one control the card carries — Log out, or Edit. */
  action?: ReactNode;
  /** Anything that belongs under the fields, such as a sign-out button. */
  children?: ReactNode;
}) {
  const nameId = useId();

  return (
    // Named by the account it is about, for the reason `Panel` is: a bare
    // <section> is no landmark, so "the identity card" was not something a
    // screen reader user or a test could ask for — and "Not yet verified"
    // appears here AND on the sidebar's user card, which is one string in two
    // true places rather than a duplicate to remove.
    <section className="profile__identity" aria-labelledby={nameId}>
      <header className="profile__identity-head">
        <span className="profile__initials" aria-hidden="true">
          {card.initials}
        </span>
        <div className="profile__identity-who">
          <h2 className="profile__identity-name" id={nameId}>
            {card.name}
          </h2>
          {card.meta && <p className="profile__identity-meta">{card.meta}</p>}
          <Tag tone={card.verified ? "verified" : "unverified"}>{card.verifiedLabel}</Tag>
        </div>
        {action}
      </header>

      <FieldList fields={card.fields} />

      {children}
    </section>
  );
}
